import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { LegalDocumentsStore } from '../../legal-documents.store';
import { SignLegalDocumentModalComponent } from '../sign-legal-document-modal/sign-legal-document-modal.component';

/**
 * The blocking signing modal: the documents to sign are shown one at a time, and the last
 * confirmation approves the whole batch, which empties the store's queue and closes the modal.
 */
@Component({
    selector: 'app-legal-document-signing',
    imports: [SignLegalDocumentModalComponent],
    template: `
        <app-sign-legal-document-modal
            [open]="open()"
            [documentType]="currentDocument()?.documentType"
            [documentUrl]="currentUrl()"
            [loading]="loading()"
            (confirmed)="approveCurrent()"
        />
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LegalDocumentSigningContainer {
    private readonly sanitizer = inject(DomSanitizer);
    private readonly store = inject(LegalDocumentsStore);

    private readonly queue = this.store.documentsToSign;
    /** Back to the first document whenever the queue changes, e.g. once the batch is approved. */
    private readonly index = linkedSignal({ source: this.queue, computation: () => 0 });

    protected readonly loading = signal(false);
    protected readonly open = computed(() => this.index() < this.queue().length);
    protected readonly currentDocument = computed(() => this.queue()[this.index()]);
    protected readonly currentUrl = computed<SafeResourceUrl | undefined>(() => {
        const document = this.currentDocument();
        // `#navpanes=0` hides the native PDF viewer pages/thumbnails sidebar.
        return document ? this.sanitizer.bypassSecurityTrustResourceUrl(`${document.url}#navpanes=0`) : undefined;
    });

    protected async approveCurrent(): Promise<void> {
        if (this.loading()) return;

        if (this.index() < this.queue().length - 1) {
            this.index.update((current) => current + 1);
            return;
        }

        this.loading.set(true);
        try {
            await this.store.approve();
        } catch {
            // The backend refused: the last document stays open so the installer can sign again.
        } finally {
            this.loading.set(false);
        }
    }
}
